import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRight, Check, ExternalLink, Minus } from 'lucide-react';
import { DemoRequestDialog } from '@/components/landing/DemoRequestDialog';
import { COMPETITOR_ICONS } from '@/components/page/competitorIcons';
import { StructuredData } from '@/components/seo/StructuredData';
import { CheckList, CtaPanel, DataTable, FaqSection, HeroActions, InShort, PageHero, PageShell, RelatedLinks, Reveal, Section, SectionHeading, StepFlow } from '@/components/page/PageKit';
import { BEATVIEW, COMMON_BUYER_QUESTIONS, COMPARISON_AREAS, buildAlternativeSchemas, competitors, getAlternatives, getCompetitorBySlug, getPageFaqs, getPageReview, getPageSeo, getPlatform } from '@/data/alternatives';
import { useSEO } from '@/hooks/useSEO';
import { ogImageFor } from '@/seo/ogImage';
import { cn } from '@/lib/utils';

export default function AlternativeDetail() {
  const { platform } = useParams<{ platform: string }>();
  const [demo, setDemo] = useState(false);
  const page = platform ? getCompetitorBySlug(platform) : undefined;
  const seo = page ? getPageSeo(page) : undefined;

  useSEO({
    title: seo?.title ?? 'Alternatives | Beatview',
    description: seo?.description ?? 'Compare Beatview with popular hiring platforms.',
    canonicalUrl: page ? `https://beatview.ai/alternatives/${page.id}` : 'https://beatview.ai/alternatives',
    ogType: 'article',
  });

  if (!page || !seo) return <Navigate to="/alternatives" replace />;

  const competitor = getPlatform(page.id);
  const alternatives = getAlternatives(page);
  const review = getPageReview(page);
  const faqs = getPageFaqs(page);
  const related = competitors.filter(c => c.id !== page.id && page.alternativeIds.includes(c.id)).slice(0, 4);

  return (
    <PageShell>
      <StructuredData data={buildAlternativeSchemas(page, ogImageFor(`https://beatview.ai/alternatives/${page.id}`))} />
      <PageHero
        breadcrumbs={[{ name: 'Alternatives', href: '/alternatives' }, { name: page.name }]}
        eyebrow={`${page.name} alternatives · Updated ${review.label}`}
        title={<>Best {page.name} alternatives <span className="bv-hero-highlight">and competitors in 2026</span></>}
        description={seo.intro}
      >
        <HeroActions onDemo={() => setDemo(true)} note={`Competitor details checked on ${page.name}’s and each vendor’s own website ${review.when}. Beatview publishes this page, and we show where other tools are the better fit.`} />
      </PageHero>

      <Section tone="white" labelledBy="overview-title">
        <div className="grid gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:items-start">
          <div>
            <InShort label={`Best ${page.name} alternative: the short answer`}>{page.quickAnswer}</InShort>
            <Reveal delay={0.05} className="mt-10">
              <h2 id="overview-title" className="font-display text-3xl font-semibold tracking-[-0.02em] text-bv-ink">What is {page.name}?</h2>
              <p className="mt-4 text-[17px] leading-8 text-bv-ink2">{page.overview}</p>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <aside aria-label={`${page.name} at a glance`} className="rounded-3xl border border-bv-blue-line bg-bv-blue-wash/40 p-6 sm:p-7">
              <p className="text-[13px] font-semibold uppercase tracking-wider text-bv-mute">{page.name} at a glance</p>
              <dl className="mt-4 space-y-4 text-[15px] leading-6">
                {[
                  ['Category', competitor.category],
                  ['Best for', competitor.bestFor],
                  ['Pricing', competitor.facts.pricing],
                  ['Free plan or trial', competitor.facts.freePlan],
                ].map(([term, value]) => (
                  <div key={term}><dt className="font-semibold text-bv-ink">{term}</dt><dd className="mt-1 text-bv-ink2">{value}{term === 'Pricing' && page.pricingHref && <> <Link to={page.pricingHref} className="font-semibold text-bv-blue hover:underline">{page.name} pricing in detail</Link></>}</dd></div>
                ))}
              </dl>
              <nav aria-label="On this page" className="mt-6 border-t border-bv-blue-line pt-5">
                <p className="text-[13px] font-semibold uppercase tracking-wider text-bv-mute">On this page</p>
                <ul className="mt-3 space-y-2 text-[15px] font-semibold text-bv-blue">
                  {[['#comparison-table', 'Comparison table'], ['#alternatives-list', `The ${alternatives.length} alternatives`], ['#beatview-vs', `Beatview vs ${page.name}`], ['#switch', `Switching from ${page.name}`], ['#faq', 'FAQ'], ['#sources', 'Sources']].map(([href, label]) => <li key={href}><a href={href} className="hover:underline">{label}</a></li>)}
                </ul>
              </nav>
            </aside>
          </Reveal>
        </div>
      </Section>

      <Section tone="wash" id="comparison-table" labelledBy="comparison-table-title">
        <SectionHeading id="comparison-table-title" eyebrow="Comparison table" title={`${page.name} alternatives compared`} description={`How ${page.name} and each alternative compare on the details buyers ask about first.`} />
        <Reveal delay={0.1} className="mt-10">
          <DataTable
            caption={`${page.name} and its alternatives compared by best fit, interview format, AI capabilities and pricing`}
            columns={['Platform', 'Best for', 'Interview format', 'AI capabilities', 'Starting price']}
            rows={[competitor, ...alternatives].map(p => [
              <span key="n" className="font-semibold text-bv-ink">{p.name}{p.id === page.id && <span className="block text-xs font-medium text-bv-mute">Compared platform</span>}{p.isBeatview && <span className="block text-xs font-medium text-bv-mute">Our product</span>}</span>,
              p.bestFor,
              p.interviewFormats,
              p.aiSummary,
              p.startingPrice,
            ])}
            minWidth={980}
          />
        </Reveal>
      </Section>

      <Section tone="white" id="alternatives-list" labelledBy="alternatives-list-title">
        <SectionHeading id="alternatives-list-title" eyebrow="The shortlist" title={`${alternatives.length} ${page.name} alternatives worth evaluating`} description="Each option with who it suits, where it is strong, and what to check before buying." />
        <ol className="mt-12 space-y-5">
          {alternatives.map((alt, i) => (
            <Reveal key={alt.id} delay={0.04}>
              <li>
                <article aria-labelledby={`alt-${alt.id}`} className={cn('rounded-3xl p-6 sm:p-8', alt.isBeatview ? 'bg-bv-ink text-white shadow-bv-deep' : 'border border-bv-blue-line bg-white')}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 id={`alt-${alt.id}`} className={cn('font-display text-2xl font-semibold', alt.isBeatview ? 'text-white' : 'text-bv-ink')}>{i + 1}. {alt.name}</h3>
                    <div className="flex flex-wrap gap-2">
                      {alt.isBeatview && <span className="bv-btn-gradient rounded-full px-3 py-1 text-xs font-semibold text-white">Our product</span>}
                      <span className={cn('rounded-full px-3 py-1 text-xs font-semibold', alt.isBeatview ? 'bg-white/10 text-white' : 'bg-bv-blue-wash text-bv-ink2')}>{alt.category}</span>
                    </div>
                  </div>
                  <p className={cn('mt-3 text-[15px] font-semibold leading-6', alt.isBeatview ? 'text-white' : 'text-bv-ink')}>Best for: <span className={cn('font-normal', alt.isBeatview ? 'text-white/75' : 'text-bv-ink2')}>{alt.bestFor}</span></p>
                  <p className={cn('mt-3 text-[16px] leading-7', alt.isBeatview ? 'text-white/75' : 'text-bv-ink2')}>{alt.summary}</p>
                  <div className="mt-6 grid gap-6 md:grid-cols-2">
                    <div>
                      <h4 className={cn('text-sm font-semibold uppercase tracking-wider', alt.isBeatview ? 'text-white/60' : 'text-bv-mute')}>Strengths</h4>
                      <CheckList tone={alt.isBeatview ? 'dark' : 'light'} className="mt-3" items={alt.strengths} />
                    </div>
                    <div>
                      <h4 className={cn('text-sm font-semibold uppercase tracking-wider', alt.isBeatview ? 'text-white/60' : 'text-bv-mute')}>Check before buying</h4>
                      <CheckList tone={alt.isBeatview ? 'dark' : 'light'} icon="minus" className="mt-3" items={alt.watchOuts} />
                    </div>
                  </div>
                  <div className={cn('mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 border-t pt-5 text-[15px]', alt.isBeatview ? 'border-white/10' : 'border-bv-blue-line')}>
                    <p className={alt.isBeatview ? 'text-white/75' : 'text-bv-ink2'}><span className={cn('font-semibold', alt.isBeatview ? 'text-white' : 'text-bv-ink')}>Pricing:</span> {alt.facts.pricing}</p>
                    <div className="ml-auto flex flex-wrap gap-4 font-semibold">
                      {competitors.some(c => c.id === alt.id) && <Link to={`/alternatives/${alt.id}`} className="inline-flex items-center gap-1 text-bv-blue hover:underline">{alt.name} alternatives <ArrowRight className="h-3.5 w-3.5" /></Link>}
                      {alt.isBeatview
                        ? <Link to="/pricing" className="inline-flex items-center gap-1 text-white hover:underline">See Beatview plans <ArrowRight className="h-3.5 w-3.5" /></Link>
                        : <a href={alt.website} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1 text-bv-ink hover:underline">Visit {alt.name} <ExternalLink className="h-3.5 w-3.5" /></a>}
                    </div>
                  </div>
                </article>
              </li>
            </Reveal>
          ))}
        </ol>
      </Section>

      <Section tone="base" labelledBy="switch-title">
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
          <SectionHeading id="switch-title" eyebrow="Why teams compare" title={`Why teams look for ${page.name} alternatives`} description="Common reasons buyers put other tools on the shortlist. None of them make a platform wrong; they make it a different fit." />
          <ol className="grid gap-4 sm:grid-cols-2">
            {page.switchTriggers.map((trigger, i) => (
              <Reveal key={trigger.title} delay={(i % 2) * 0.06}>
                <li className="h-full rounded-2xl border border-bv-blue-line bg-white p-6">
                  <span className="bv-hero-highlight font-display text-2xl font-semibold">0{i + 1}</span>
                  <h3 className="mt-3 font-display text-lg font-semibold text-bv-ink">{trigger.title}</h3>
                  <p className="mt-2 text-[15px] leading-6 text-bv-ink2">{trigger.body}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </Section>

      <Section tone="wash" id="beatview-vs" labelledBy="beatview-vs-title">
        <SectionHeading id="beatview-vs-title" eyebrow="Head to head" title={`Beatview vs ${page.name}`} description={page.difference} />
        <Reveal delay={0.1} className="mt-10">
          <DataTable caption={`Beatview compared with ${page.name}`} columns={['Decision area', 'Beatview', page.name]} rows={COMPARISON_AREAS.map(area => [area.label, BEATVIEW.facts[area.key], competitor.facts[area.key]])} highlight={1} minWidth={820} />
        </Reveal>
      </Section>

      <Section tone="white" labelledBy="fit-title">
        <h2 id="fit-title" className="sr-only">Which platform fits</h2>
        <div className="grid gap-5 md:grid-cols-2">
          <Reveal>
            <article className="h-full rounded-3xl bg-bv-ink p-7 text-white shadow-bv-deep sm:p-9">
              <span className="bv-btn-gradient flex h-11 w-11 items-center justify-center rounded-xl"><Check className="h-5 w-5" strokeWidth={3} /></span>
              <h3 className="mt-5 font-display text-2xl font-semibold">Choose Beatview when</h3>
              <CheckList tone="dark" className="mt-6" items={page.chooseBeatview} />
            </article>
          </Reveal>
          <Reveal delay={0.08}>
            <article className="h-full rounded-3xl border border-bv-blue-line bg-white p-7 sm:p-9">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-bv-blue-wash text-bv-ink2"><Minus className="h-5 w-5" strokeWidth={3} /></span>
              <h3 className="mt-5 font-display text-2xl font-semibold text-bv-ink">Keep {page.name} when</h3>
              <CheckList className="mt-6" items={page.keepCompetitor} />
            </article>
          </Reveal>
        </div>
      </Section>

      <Section tone="wash" id="switch" labelledBy="how-switch-title">
        <SectionHeading id="how-switch-title" eyebrow="Switching" title={`How to switch from ${page.name} without a gap in hiring`} description={`Run Beatview beside ${page.name} on one live role first, and move over only when the evidence says so.`} />
        <div className="mt-12">
          <StepFlow steps={[
            { title: `List what ${page.name} does for you`, body: `Write down the stages ${page.name} covers today, your monthly volume, the integrations you rely on and when your contract renews.` },
            { title: 'Rebuild one role in Beatview', body: 'Set up the job, its screening questions, a score threshold, the stage that acts on it and the interview it sends. The Free plan covers one active job; AI video interviews need Pro.' },
            { title: 'Run both on the same vacancy', body: 'Compare the two shortlists, the hours your consultants spent and how many candidates finished the interview. Decide on those numbers.' },
            { title: 'Connect your ATS and move over', body: 'Connect Beatview to your ATS through Zapier on Enterprise, or ask us to enable a native integration. Keep your old tool until the contract ends.' },
          ]} />
        </div>
      </Section>

      <Section tone="dark" labelledBy="questions-title">
        <SectionHeading tone="dark" id="questions-title" eyebrow="Buyer checklist" title="Questions to ask any vendor before choosing" />
        <ol className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...COMMON_BUYER_QUESTIONS, ...page.buyerQuestions].map((question, i) => (
            <Reveal key={question} delay={(i % 3) * 0.06}>
              <li className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-6"><span className="bv-text-gradient-dark font-display text-2xl font-semibold">0{i + 1}</span><p className="mt-3 font-semibold leading-7">{question}</p></li>
            </Reveal>
          ))}
        </ol>
      </Section>

      <div id="faq" className="scroll-mt-24">
        <FaqSection title={`${page.name} alternatives: frequently asked questions`} items={faqs} idPrefix={`${page.id}-faq`} tone="base" />
      </div>

      <Section tone="white" narrow id="sources" labelledBy="sources-title">
        <SectionHeading id="sources-title" eyebrow="Sources and method" title="How we compared" description={`We reviewed each vendor’s own pricing and product pages ${review.when}. Where a vendor does not publish a detail, we say so rather than guess. Features and prices change, so confirm current terms with each vendor.`} />
        <ul className="mt-8 grid gap-3 sm:grid-cols-2">
          {[competitor, ...alternatives.filter(a => !a.isBeatview)].flatMap(p => p.sources).map(source => (
            <li key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer nofollow" className="group flex items-center justify-between gap-3 rounded-2xl border border-bv-blue-line bg-white px-5 py-4 font-semibold text-bv-ink transition-colors hover:border-bv-blue/40 hover:text-bv-blue">{source.label}<ExternalLink className="h-4 w-4 shrink-0" /></a></li>
          ))}
        </ul>
      </Section>

      <RelatedLinks eyebrow="More comparisons" title="Compare other hiring platforms" tone="base" items={related.map(c => ({ title: `${c.name} alternatives`, body: getPlatform(c.id).bestFor, href: `/alternatives/${c.id}`, icon: COMPETITOR_ICONS[c.id] }))} />

      <CtaPanel eyebrow={`Comparing ${page.name}?`} title={<>{page.ctaHeadline}</>} description={`Start with one active job on the Free plan, or walk through how Beatview’s screening and interview workflow compares with ${page.name} on your own role.`} onDemo={() => setDemo(true)} secondary={{ label: 'Explore the agency workflow', href: '/for-recruiting-agencies' }} />
      <DemoRequestDialog open={demo} onOpenChange={setDemo} />
    </PageShell>
  );
}
